import { createFileRoute } from "@tanstack/react-router";
import { Plus, Edit2, Trash2, Loader2, Ticket, Percent, Calendar } from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { API_BASE_URL } from "@/lib/api";

export const Route = createFileRoute("/admin/coupons")({
  head: () => ({
    meta: [
      { title: "Manage Coupons — Royal Lumina" },
    ],
  }),
  component: AdminCoupons,
});

type FormCoupon = {
  id?: string;
  code: string;
  discountType: "percentage" | "fixed";
  discountValue: number;
  minOrderAmount: number;
  startDate: string;
  endDate: string;
  isActive: boolean;
  usageLimit: string; // string in form, converted to number/null
};

const emptyForm: FormCoupon = {
  code: "",
  discountType: "percentage",
  discountValue: 10,
  minOrderAmount: 0,
  startDate: new Date().toISOString().split("T")[0],
  endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0], // 30 days from now
  isActive: true,
  usageLimit: "",
};

const staticCouponsData = [
  {
    id: "coupon-1",
    code: "LUMINA10",
    discountType: "percentage",
    discountValue: 10,
    minOrderAmount: 0,
    startDate: new Date().toISOString(),
    endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
    isActive: true,
    usageLimit: null,
    usageCount: 23,
    created_at: new Date().toISOString()
  },
  {
    id: "coupon-2",
    code: "FESTIVE500",
    discountType: "fixed",
    discountValue: 500,
    minOrderAmount: 2999,
    startDate: new Date().toISOString(),
    endDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
    isActive: true,
    usageLimit: 100,
    usageCount: 45,
    created_at: new Date().toISOString()
  }
];

function AdminCoupons() {
  const [coupons, setCoupons] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState<FormCoupon>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("supabase.auth.token") || "";
      const res = await fetch(`${API_BASE_URL}/api/coupons`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setCoupons(data);
      } else {
        setCoupons(staticCouponsData);
      }
    } catch (err) {
      setCoupons(staticCouponsData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const handleOpenCreate = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (c: any) => {
    setFormData({
      code: c.code,
      discountType: c.discountType,
      discountValue: c.discountValue,
      minOrderAmount: c.minOrderAmount,
      startDate: new Date(c.startDate).toISOString().split("T")[0],
      endDate: new Date(c.endDate).toISOString().split("T")[0],
      isActive: c.isActive,
      usageLimit: c.usageLimit !== null ? String(c.usageLimit) : "",
    });
    setEditingId(c.id);
    setIsDialogOpen(true);
  };

  const handleDelete = async (coupon: any) => {
    if (!confirm(`Are you sure you want to delete coupon code "${coupon.code}"?`)) return;

    try {
      const token = localStorage.getItem("supabase.auth.token") || "";
      const res = await fetch(`${API_BASE_URL}/api/coupons/${coupon.id}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (res.ok) {
        toast.success("Coupon deleted successfully");
        fetchCoupons();
      } else {
        setCoupons((prev) => prev.filter((c) => c.id !== coupon.id));
        toast.success("Coupon removed (Local state)");
      }
    } catch (err) {
      setCoupons((prev) => prev.filter((c) => c.id !== coupon.id));
      toast.success("Coupon removed (Local state fallback)");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.code.trim()) {
      toast.error("Please enter a coupon code");
      return;
    }

    if (formData.discountValue <= 0) {
      toast.error("Discount value must be greater than zero");
      return;
    }

    if (formData.discountType === "percentage" && formData.discountValue > 100) {
      toast.error("Percentage discount cannot exceed 100%");
      return;
    }

    const payload = {
      code: formData.code.trim().toUpperCase(),
      discountType: formData.discountType,
      discountValue: Number(formData.discountValue),
      minOrderAmount: Number(formData.minOrderAmount),
      startDate: new Date(formData.startDate).toISOString(),
      endDate: new Date(formData.endDate).toISOString(),
      isActive: formData.isActive,
      usageLimit: formData.usageLimit.trim() ? Number(formData.usageLimit) : null,
    };

    try {
      const token = localStorage.getItem("supabase.auth.token") || "";
      const url = editingId 
        ? `${API_BASE_URL}/api/coupons/${editingId}`
        : `${API_BASE_URL}/api/coupons`;
      
      const method = editingId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success(editingId ? "Coupon updated successfully" : "Coupon created successfully");
        setIsDialogOpen(false);
        fetchCoupons();
      } else {
        const errorData = await res.json();
        throw new Error(errorData.message || "API call failed");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to save coupon");

      // Local state simulation
      const mockResult = {
        ...payload,
        id: editingId || `coupon_${Date.now()}`,
        usageCount: editingId ? (coupons.find(c => c.id === editingId)?.usageCount || 0) : 0,
        created_at: new Date().toISOString()
      };

      if (editingId) {
        setCoupons((prev) => prev.map((c) => c.id === editingId ? mockResult : c));
        toast.success("Coupon updated (Local simulation)");
      } else {
        setCoupons((prev) => [...prev, mockResult]);
        toast.success("Coupon added (Local simulation)");
      }
      setIsDialogOpen(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const isExpired = (endDateStr: string) => {
    return new Date(endDateStr) < new Date();
  };

  return (
    <div className="space-y-8">
      {/* Header bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-display text-4xl tracking-tight text-primary">Coupon Management</h2>
          <p className="text-sm text-muted-foreground mt-1">Create and manage discounts for your customers.</p>
        </div>
        <button
          onClick={handleOpenCreate}
          className="btn-gold self-start sm:self-auto"
        >
          <Plus className="h-4 w-4" />
          <span>Create Coupon</span>
        </button>
      </div>

      {/* Coupons Table */}
      {loading ? (
        <div className="h-64 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : (
        <div className="bg-card border border-border rounded-[24px] overflow-hidden shadow-luxe">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="bg-muted/40 border-b border-border text-[0.65rem] uppercase tracking-wider text-muted-foreground font-semibold">
                  <th className="px-6 py-4">Coupon Code</th>
                  <th className="px-6 py-4">Discount</th>
                  <th className="px-6 py-4">Min. Spend</th>
                  <th className="px-6 py-4">Duration</th>
                  <th className="px-6 py-4 text-center">Status</th>
                  <th className="px-6 py-4 text-center">Usage</th>
                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {coupons.map((c) => {
                  const expired = isExpired(c.endDate);
                  const active = c.isActive && !expired;
                  return (
                    <tr key={c.id} className="hover:bg-muted/10 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${active ? 'bg-gold/10 text-gold' : 'bg-muted text-muted-foreground'}`}>
                            <Ticket className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="font-semibold text-primary">{c.code}</div>
                            <div className="text-[0.65rem] text-muted-foreground mt-0.5">
                              Created on {formatDate(c.created_at || new Date())}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5 font-medium text-primary">
                          {c.discountType === "percentage" ? (
                            <>
                              <span>{c.discountValue}%</span>
                              <Percent className="h-3 w-3 text-muted-foreground" />
                            </>
                          ) : (
                            <span>₹{c.discountValue.toLocaleString("en-IN")}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {c.minOrderAmount > 0 ? `₹${c.minOrderAmount.toLocaleString("en-IN")}` : "None"}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        <div className="flex items-center gap-1.5 text-xs">
                          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                          <span>{formatDate(c.startDate)} - {formatDate(c.endDate)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          active 
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200" 
                            : expired 
                              ? "bg-rose-50 text-rose-700 border border-rose-200" 
                              : "bg-amber-50 text-amber-700 border border-amber-200"
                        }`}>
                          {active ? "Active" : expired ? "Expired" : "Disabled"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center text-muted-foreground font-medium">
                        {c.usageCount} {c.usageLimit !== null ? `/ ${c.usageLimit}` : ""}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleOpenEdit(c)}
                            className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-primary transition-colors"
                            title="Edit Coupon"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(c)}
                            className="p-2 rounded-lg hover:bg-rose-50 text-muted-foreground hover:text-rose-600 transition-colors"
                            title="Delete Coupon"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add / Edit Dialog */}
      {isDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-primary/40 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-[24px] max-w-lg w-full p-8 shadow-luxe relative overflow-y-auto max-h-[90vh]">
            <h3 className="font-display text-2xl text-primary mb-6">
              {editingId ? "Edit Coupon Details" : "Create New Coupon"}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Coupon Code</span>
                  <input
                    type="text"
                    required
                    placeholder="WINTER20"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold font-mono"
                  />
                </label>

                <label className="block">
                  <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Discount Type</span>
                  <select
                    value={formData.discountType}
                    onChange={(e) => setFormData({ ...formData, discountType: e.target.value as any })}
                    className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Discount Value</span>
                  <input
                    type="number"
                    required
                    min="1"
                    value={formData.discountValue}
                    onChange={(e) => setFormData({ ...formData, discountValue: Number(e.target.value) })}
                    className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                  />
                </label>

                <label className="block">
                  <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Min Order Amount (₹)</span>
                  <input
                    type="number"
                    min="0"
                    value={formData.minOrderAmount}
                    onChange={(e) => setFormData({ ...formData, minOrderAmount: Number(e.target.value) })}
                    className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                  />
                </label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Start Date</span>
                  <input
                    type="date"
                    required
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                  />
                </label>

                <label className="block">
                  <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">End Date</span>
                  <input
                    type="date"
                    required
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                  />
                </label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Usage Limit (Optional)</span>
                  <input
                    type="number"
                    placeholder="Unlimited"
                    min="1"
                    value={formData.usageLimit}
                    onChange={(e) => setFormData({ ...formData, usageLimit: e.target.value })}
                    className="mt-2 w-full h-11 px-4 bg-card border border-border rounded-[12px] text-sm focus:outline-none focus:border-gold"
                  />
                </label>

                <div className="flex flex-col justify-center">
                  <span className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground">Active Status</span>
                  <label className="mt-3 flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="h-4 w-4 accent-gold rounded border-border"
                    />
                    <span className="text-sm text-primary">Coupon is active</span>
                  </label>
                </div>
              </div>

              <div className="mt-8 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsDialogOpen(false)}
                  className="btn-outline-dark h-11 py-0 px-5 text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-gold h-11 py-0 px-6 text-xs"
                >
                  Save Coupon
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
